import {createContext, useContext, useEffect, useRef, useState} from 'react';
import 'firebase/compat/auth';
import {functions} from 'firebaseApp';
import {httpsCallable} from 'firebase/functions';

export const ONE_SECOND_MS = 1000;
export const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;
export const ONE_WEEK_MS = 7 * ONE_DAY_MS;

/**       
 * This context provides the server time to the client.
 * Caution: because of internet delays it could be incorrect up to
 * about 1 second. Do not use it for anything which needs more precise
 * timing.
 *
 * Stores the server time in the `serverDelta variable. It is the difference
 * in ms between the server time and the client time.
 */
export interface TimeContextType {
  /**
   * 
   * @returns 
   */
  getServerTimeMs: () => number | undefined;
  serverTimeDelta: number | undefined;
  hasEditedCodeSinceUpdateRef?: any;
  serverTimeLoading,

}

export function useServerTime(refreshRate: number = 1000): number | undefined {
  const {serverTimeDelta} = useContext(TimeContext);
  const [serverTimeMs, setServerTimeMs] = useState(Date.now() + serverTimeDelta);

  // once we have the delta, start updating the time
  useEffect(() => {
    if (serverTimeDelta === undefined) {
      return;
    }
    setServerTimeMs(Date.now() + serverTimeDelta);
    const interval = setInterval(() => {
      setServerTimeMs(Date.now() + serverTimeDelta);
    }, refreshRate); // refresh every second
    return () => clearInterval(interval);
  }, [serverTimeDelta]);

  return serverTimeMs;
};

export function useServerTimeOnce(): number | undefined {
  const {getServerTimeMs} = useContext(TimeContext);
  const [serverTimeMs, setServerTimeMs] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (getServerTimeMs === undefined) {
      return;
    }
    setServerTimeMs(getServerTimeMs());
  }, [getServerTimeMs]);

  return serverTimeMs;
};

const defaultData: TimeContextType = {
  getServerTimeMs: undefined,
  serverTimeDelta: undefined,
  hasEditedCodeSinceUpdateRef: undefined,
  serverTimeLoading: true,
};

// TODO: Copy this to the server
type GetServerTimeRequest = void;
interface GetServerTimeResponse {
  serverTime: number;
}

export const TimeContext = createContext<TimeContextType>(defaultData);
export const TimeProvider = ({children}) => {
  const [serverTimeDelta, setServerDelta] = useState<number | undefined>(
    undefined,
  );
  const [serverTimeLoading, setServerLoading] = useState(true);
  const hasEditedCodeSinceUpdateRef = useRef(undefined);

  useEffect(() => {
    const getServerTime = httpsCallable<
      GetServerTimeRequest,
      GetServerTimeResponse
    >(functions, 'getServerTime');
    getServerTime().then(result => {
      const serverTime = result.data.serverTime;
      const clientTime = Date.now();
      const serverDelta = serverTime - clientTime;
      setServerDelta(serverDelta);
      setServerLoading(false);
    });
  }, []);

  const getServerTimeMs = (): number | undefined => {
    if (serverTimeDelta === undefined) {
      return undefined;
    }
    return Date.now() + serverTimeDelta;
  };

  return (
    <TimeContext.Provider
      value={{
        getServerTimeMs,
        serverTimeDelta,
        hasEditedCodeSinceUpdateRef,
        serverTimeLoading,
      }}
    >
      {children}
    </TimeContext.Provider>
  );
};
